import { useEffect, useState } from "react";
import { Search, Loader2, MapPin, Plus, Check } from "lucide-react";
import { api } from "../api";
import { useAdminAuth } from "../AdminAuthContext";
import { Panel, PageHead, Loading, LoadError, Badge, VisibilityBadge, fmtDate } from "../ui";

const fieldCls =
    "w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors";

export const AdminDestinations = () => {
    const { user } = useAdminAuth();
    const isAdmin = user?.role === "ADMIN";
    const [search, setSearch] = useState("");
    const [items, setItems] = useState(null);
    const [error, setError] = useState("");
    const [form, setForm] = useState({ name: "", city: "", state: "", description: "" });
    const [creating, setCreating] = useState(false);
    const [formError, setFormError] = useState("");
    const [busyId, setBusyId] = useState("");

    const load = () =>
        api
            .get("/admin/destinations")
            .then((res) => {
                setItems(res.data.items);
                setError("");
            })
            .catch(() => setError("Could not load destinations. Please refresh."));

    useEffect(() => {
        load();
    }, []);

    const toggle = async (d, field) => {
        setBusyId(d.id + field);
        try {
            await api.patch(`/admin/destinations/${d.id}`, { [field]: !d[field] });
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || "Could not update this destination.");
        } finally {
            setBusyId("");
        }
    };

    const create = async (e) => {
        e.preventDefault();
        setCreating(true);
        setFormError("");
        try {
            await api.post("/admin/destinations", form);
            setForm({ name: "", city: "", state: "", description: "" });
            await load();
        } catch (err) {
            setFormError(err.response?.data?.detail || "Could not add the destination.");
        } finally {
            setCreating(false);
        }
    };

    if (error && !items) return <LoadError message={error} />;
    if (!items) return <Loading />;

    const filtered = items.filter(
        (d) => !search.trim() || d.name.toLowerCase().includes(search.trim().toLowerCase()) || d.city.toLowerCase().includes(search.trim().toLowerCase())
    );

    return (
        <div data-testid="admin-destinations-page">
            <PageHead
                title="Destinations"
                subtitle="Regions where UniversHeal coordinates care. Future destinations stay hidden until explicitly activated."
            />

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64716D]" />
                    <input
                        data-testid="destinations-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search destinations…"
                        className="w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] pl-9 pr-4 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors"
                    />
                </div>
            </div>

            <div className="mt-6 grid lg:grid-cols-3 gap-6">
                <Panel className="lg:col-span-2 overflow-hidden" data-testid="destinations-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-[#1A2E2A] bg-[#0E1A17]">
                                    {["Destination", "City", "Active", "Public visibility", "Updated", ""].map((h) => (
                                        <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#64716D] whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((d) => (
                                    <tr key={d.id || d.name} data-testid={`destination-row-${d.name.toLowerCase().replace(/\s+/g, "-")}`} className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors">
                                        <td className="px-4 py-3 text-sm text-[#F7F5EF] font-medium">{d.name}</td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{d.city}{d.state ? `, ${d.state}` : ""}</td>
                                        <td className="px-4 py-3">
                                            {d.active ? (
                                                <Badge className="border-emerald-800/70 bg-emerald-950/60 text-emerald-300">Active</Badge>
                                            ) : (
                                                <Badge className="border-zinc-700/80 bg-zinc-800/80 text-zinc-400">Inactive</Badge>
                                            )}
                                        </td>
                                        <td className="px-4 py-3"><VisibilityBadge visible={d.public_visible} /></td>
                                        <td className="px-4 py-3 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(d.updated_at || d.created_at)}</td>
                                        <td className="px-4 py-3">
                                            {isAdmin ? (
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        data-testid={`destination-toggle-active-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                                                        disabled={busyId === d.id + "active"}
                                                        onClick={() => toggle(d, "active")}
                                                        className="rounded-lg border border-[#1D322E] px-3 py-1.5 text-xs font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors disabled:opacity-50"
                                                    >
                                                        {busyId === d.id + "active" ? "…" : d.active ? "Deactivate" : "Activate"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        data-testid={`destination-toggle-public-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                                                        disabled={busyId === d.id + "public_visible"}
                                                        onClick={() => toggle(d, "public_visible")}
                                                        className="rounded-lg border border-[#1D322E] px-3 py-1.5 text-xs font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors disabled:opacity-50"
                                                    >
                                                        {busyId === d.id + "public_visible" ? "…" : d.public_visible ? "Make private" : "Make public"}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-xs text-[#64716D]">Read-only</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {filtered.length === 0 && (
                        <div className="py-12 text-center" data-testid="destinations-empty">
                            <MapPin className="w-8 h-8 mx-auto text-[#64716D]" />
                            <p className="mt-3 text-sm text-[#A3B8B0]">No destinations match this search.</p>
                        </div>
                    )}
                </Panel>

                {isAdmin && (
                    <Panel className="p-5 h-fit" data-testid="destination-create-form">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <Plus className="w-4 h-4" /> Add destination
                        </h2>
                        <p className="mt-2 text-xs text-[#64716D]">
                            New destinations start inactive and hidden from the public website.
                        </p>
                        <form onSubmit={create} className="mt-4 space-y-4">
                            <input data-testid="destination-create-name" required placeholder="Name (e.g. Bengaluru)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={fieldCls} />
                            <input data-testid="destination-create-city" required placeholder="City / region *" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className={fieldCls} />
                            <input data-testid="destination-create-state" placeholder="State (optional)" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} className={fieldCls} />
                            <textarea data-testid="destination-create-description" rows={3} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={fieldCls} />
                            {formError && (
                                <p role="alert" data-testid="destination-create-error" className="rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                                    {formError}
                                </p>
                            )}
                            <button type="submit" data-testid="destination-create-submit" disabled={creating} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed">
                                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                {creating ? "Adding…" : "Add destination"}
                            </button>
                        </form>
                    </Panel>
                )}
            </div>
        </div>
    );
};
